import type { ReactNode } from "react";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import {
  Box,
  Chip,
  Divider,
  Paper,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  useTheme,
} from "@mui/material";
import type { Palette } from "@mui/material/styles";
import { useTranslation } from "react-i18next";
import { match } from "ts-pattern";
import type { Promisable } from "type-fest";

import {
  levelOf,
  sectionsOf,
  tallyOf,
  type GroupCategory,
  type ItemChange,
  type NotAnsweredInDomain,
  type ShownItem,
  type AssessmentSummaryView as Summary,
  type SummaryColumn,
  type SummaryGrouping,
  type SummaryItem,
  type SummarySection,
} from "@thalamos/haven-common";

import { VISUALLY_HIDDEN } from "../../theme/visuallyHidden.js";
import {
  RatingBadge,
  swatchOf,
  useAnswerWords,
} from "../RatingBadge/RatingBadge.js";

/** How each change is marked: muted for what stayed, warm for what grew, cool for what improved. */
const changeMarkByItemChange: Record<
  ItemChange,
  { tone: keyof Palette["change"]; bold?: boolean }
> = {
  same: { tone: "muted" },
  continuing: { tone: "muted" },
  changed: { tone: "warm" },
  new: { tone: "warm" },
  escalated: { tone: "hot" },
  weakened: { tone: "warm" },
  eased: { tone: "cool" },
  resolved: { tone: "cool", bold: true },
  built: { tone: "cool", bold: true },
  not_rated: { tone: "blank" },
  not_answered: { tone: "blank" },
};

/** What a screen reader hears of an item's ratings, which the badges only draw. */
const RatingsSpoken = ({ item }: { item: SummaryItem }) => {
  const { t } = useTranslation();
  const wordOf = useAnswerWords();
  const { scale, change } = item;
  const spokenAs = match(scale)
    .with("yes_no", () => "answer" as const)
    .with("rating", () => "rating" as const)
    .exhaustive();
  const rating =
    item.rating === undefined
      ? undefined
      : wordOf({ scale, rating: item.rating });
  const previous =
    item.previous === undefined
      ? undefined
      : wordOf({ scale, rating: item.previous });

  const spoken = (): string => {
    if (change === undefined) {
      return t(`assessment.summary.${spokenAs}.now`, { rating });
    }

    if (rating === undefined) {
      return change === "not_answered"
        ? t(`assessment.summary.${spokenAs}.notAnsweredNow`, { previous })
        : t(`assessment.summary.${spokenAs}.notRatedNow`, { previous });
    }

    if (previous !== undefined) {
      return t(`assessment.summary.${spokenAs}.moved`, { previous, rating });
    }

    return item.notAnsweredBefore === true
      ? t(`assessment.summary.${spokenAs}.notAnsweredBefore`, { rating })
      : t(`assessment.summary.${spokenAs}.notRatedBefore`, { rating });
  };

  return (
    <Box component="span" sx={VISUALLY_HIDDEN}>
      {`${spoken()}: `}
    </Box>
  );
};

const ItemRow = ({
  category,
  item,
  showDomain,
  departed,
}: {
  category: GroupCategory;
  item: SummaryItem;
  showDomain: boolean;
  departed: boolean;
}) => {
  const { t } = useTranslation();
  const { change } = item;
  const comparing = change !== undefined;

  return (
    <Box
      component="li"
      sx={{ display: "flex", alignItems: "center", gap: 1, py: 0.5 }}
    >
      {comparing ? (
        <Stack direction="row" alignItems="center" spacing={0.5} aria-hidden>
          <RatingBadge
            category={category}
            rating={item.previous}
            scale={item.scale}
            size="small"
          />
          <ArrowForwardIcon
            sx={{ fontSize: "0.8125rem", color: "text.secondary" }}
          />
        </Stack>
      ) : null}
      <RatingBadge
        category={category}
        rating={item.rating}
        scale={item.scale}
      />
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Typography
          component="span"
          variant="body2"
          sx={{ display: "block", fontWeight: departed ? "normal" : 500 }}
        >
          <RatingsSpoken item={item} />
          {item.title}
        </Typography>
        {showDomain ? (
          <Typography
            component="span"
            variant="caption"
            color="text.secondary"
            sx={{ display: "block", lineHeight: 1.4 }}
          >
            {item.domain}
          </Typography>
        ) : null}
      </Box>
      {change === undefined ? null : (
        <Chip
          size="small"
          label={t(`assessment.summary.change.${change}`)}
          variant={
            change === "not_rated" || change === "not_answered"
              ? "outlined"
              : "filled"
          }
          sx={(theme) => {
            const mark = changeMarkByItemChange[change];
            const swatch = theme.palette.change[mark.tone];

            return {
              height: 20,
              fontSize: "0.6875rem",
              bgcolor: swatch.background,
              color: swatch.color,
              fontWeight: mark.bold === true ? 700 : 500,
            };
          }}
        />
      )}
    </Box>
  );
};

const SectionHeading = ({
  category,
  section,
}: {
  category: GroupCategory;
  section: SummarySection;
}) => {
  const { t } = useTranslation();
  const wordOf = useAnswerWords();

  const heading = match(section.heading)
    .with({ kind: "rating", scale: "yes_no" }, ({ rating, scale }) =>
      wordOf({ scale, rating }),
    )
    .with({ kind: "rating", scale: "rating" }, ({ rating }) =>
      t("assessment.summary.section.rating", { rating }),
    )
    .with({ kind: "domain" }, ({ title }) => title)
    .with({ kind: "departed" }, () =>
      t(`assessment.summary.section.departed.${category}`),
    )
    .exhaustive();

  return (
    <Stack direction="row" justifyContent="space-between" alignItems="baseline">
      <Typography
        variant="overline"
        component="h3"
        sx={{
          fontSize: "0.6875rem",
          fontWeight: "bold",
          lineHeight: 1.8,
          color: "text.secondary",
        }}
      >
        {heading}
      </Typography>
      <Typography
        variant="caption"
        sx={{ fontWeight: "bold", color: "text.secondary" }}
      >
        {section.items.length}
      </Typography>
    </Stack>
  );
};

/** The line under a column's title: what it holds, or how it moved in a comparison. */
const useColumnSubtitle = (column: SummaryColumn, comparing: boolean) => {
  const { t } = useTranslation();
  const tally = tallyOf(column);
  const parts = (): string[] => {
    // A column empty in both has nothing to tally; its empty state says so.
    if (comparing && column.shown.length + column.departed.length === 0) {
      return [];
    }

    if (comparing) {
      return match(column.category)
        .with("history", () => [
          // Everything not the same counts as changed, new and no longer
          // rated included.
          t("assessment.summary.tally.changed", {
            count: column.shown.length + column.departed.length - tally.same,
          }),
        ])
        .with("strengths", () => [
          t("assessment.summary.tally.built", { count: tally.built }),
        ])
        .with("needs", "caregiver", () =>
          (
            ["resolved", "new", "escalated", "eased", "continuing"] as const
          ).map((change) =>
            t(`assessment.summary.tally.${change}`, { count: tally[change] }),
          ),
        )
        .exhaustive();
    }

    const top = Math.max(...column.shown.map((item) => levelOf(item)));
    const atTop = column.shown.filter((item) => levelOf(item) === top);
    // A yes stands level with a 3, so the top may hold both, each counted as
    // what it is.
    const yeses = atTop.filter(({ scale }) => scale === "yes_no").length;
    const rated = atTop.length - yeses;
    const domains = new Set(column.shown.map(({ group }) => group)).size;

    return column.shown.length === 0
      ? []
      : [
          ...(rated === 0
            ? []
            : [
                t("assessment.summary.topRated", {
                  count: rated,
                  rating: top,
                }),
              ]),
          ...(yeses === 0
            ? []
            : [t("assessment.summary.topAnsweredYes", { count: yeses })]),
          ...(column.category === "needs" || column.category === "caregiver"
            ? [t("assessment.summary.domains", { count: domains })]
            : []),
        ];
  };

  return [
    ...parts(),
    ...(column.category === "history"
      ? [t("assessment.summary.neverActioned")]
      : []),
  ].join(" · ");
};

const ColumnCard = ({
  column,
  grouping,
  comparing,
}: {
  column: SummaryColumn;
  grouping: SummaryGrouping;
  comparing: boolean;
}) => {
  const { t } = useTranslation();
  const titleId = `summary-column-${column.category}`;
  const subtitle = useColumnSubtitle(column, comparing);
  const sections = sectionsOf(column, grouping);

  return (
    <Paper
      component="section"
      aria-labelledby={titleId}
      variant="outlined"
      sx={{ borderRadius: 2, overflow: "hidden", alignSelf: "start" }}
    >
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="flex-start"
        spacing={2}
        sx={(theme) => ({
          px: 2,
          py: 1.5,
          bgcolor:
            theme.palette.rating.category[column.category].headingBackground,
        })}
      >
        <Box>
          <Typography
            variant="h6"
            component="h2"
            id={titleId}
            sx={{ fontSize: "1rem", fontWeight: 600 }}
          >
            {t(`assessment.summary.column.${column.category}`)}
          </Typography>
          {subtitle === "" ? null : (
            <Typography variant="caption" component="p" color="text.secondary">
              {subtitle}
            </Typography>
          )}
        </Box>
        <Typography component="p" sx={{ whiteSpace: "nowrap", lineHeight: 1 }}>
          <Box component="span" aria-hidden>
            {column.previousCount === undefined ? null : (
              <Typography
                component="span"
                variant="body2"
                color="text.secondary"
                sx={{ mr: 0.5 }}
              >
                {t("assessment.summary.countFrom", {
                  from: column.previousCount,
                })}
              </Typography>
            )}
            <Typography
              component="span"
              sx={{ fontSize: "1.5rem", fontWeight: "bold" }}
            >
              {column.shown.length}
            </Typography>
          </Box>
          <Box component="span" sx={VISUALLY_HIDDEN}>
            {column.previousCount === undefined
              ? t("assessment.summary.count", { count: column.shown.length })
              : t("assessment.summary.countMoved", {
                  from: column.previousCount,
                  to: column.shown.length,
                })}
          </Box>
        </Typography>
      </Stack>
      {sections.length === 0 ? (
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ px: 2, py: 1.5 }}
        >
          {t(`assessment.summary.empty.${column.category}`)}
        </Typography>
      ) : (
        <Stack divider={<Divider />}>
          {sections.map((section) => (
            <Box key={section.key} sx={{ px: 2, py: 0.75 }}>
              <SectionHeading category={column.category} section={section} />
              <Box component="ul" sx={{ listStyle: "none", m: 0, p: 0 }}>
                {section.items.map((item) => (
                  <ItemRow
                    key={item.item}
                    category={column.category}
                    item={item}
                    showDomain={!comparing && grouping === "rating"}
                    departed={section.heading.kind === "departed"}
                  />
                ))}
              </Box>
            </Box>
          ))}
        </Stack>
      )}
    </Paper>
  );
};

/** The items an assessment was sent with not answered, counted by the domain they are in. */
const NotAnsweredPanel = ({
  domains,
  count,
}: {
  domains: readonly NotAnsweredInDomain[];
  count: number;
}) => {
  const { t } = useTranslation();

  return (
    <Paper
      component="section"
      aria-labelledby="summary-not-answered"
      variant="outlined"
      sx={{ borderRadius: 2, px: 2, py: 1.5 }}
    >
      <Typography
        id="summary-not-answered"
        component="h2"
        variant="body2"
        sx={{ mb: 1 }}
      >
        <Box component="span" sx={{ fontWeight: 600 }}>
          {t("assessment.summary.footer.notAnswered")}
        </Box>
        <Box component="span" sx={{ color: "text.secondary" }}>
          {` · ${String(count)}`}
        </Box>
      </Typography>
      <Box
        component="ul"
        sx={{
          listStyle: "none",
          m: 0,
          p: 0,
          display: "flex",
          flexWrap: "wrap",
          gap: 0.75,
        }}
      >
        {domains.map(({ group, title, count: inDomain }) => (
          <Box component="li" key={group}>
            <Chip
              size="small"
              variant="outlined"
              label={t("assessment.summary.footer.notAnsweredInDomain", {
                domain: title,
                count: inDomain,
              })}
            />
          </Box>
        ))}
      </Box>
    </Paper>
  );
};

/** A strip beneath the columns of what a single assessment shows but does not put forward. */
const Strip = ({
  id,
  title,
  rated,
  items,
  category,
  withRatings,
}: {
  id: string;
  title: string;
  rated: string;
  items: readonly ShownItem[];
  category: GroupCategory;
  /** Whether each chip says its rating, for a strip whose items are not all rated alike. */
  withRatings: boolean;
}) => {
  const { t } = useTranslation();
  const wordOf = useAnswerWords();
  const colours = useTheme().palette.rating;

  return (
    <Paper
      component="section"
      aria-labelledby={id}
      variant="outlined"
      sx={{ borderRadius: 2, px: 2, py: 1.5 }}
    >
      <Typography id={id} component="h2" variant="body2" sx={{ mb: 1 }}>
        <Box component="span" sx={{ fontWeight: 600 }}>
          {title}
        </Box>
        <Box component="span" sx={{ color: "text.secondary" }}>
          {` · ${rated} · ${String(items.length)}`}
        </Box>
      </Typography>
      {items.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          {t("assessment.summary.footer.none")}
        </Typography>
      ) : (
        <Box
          component="ul"
          sx={{
            listStyle: "none",
            m: 0,
            p: 0,
            display: "flex",
            flexWrap: "wrap",
            gap: 0.75,
          }}
        >
          {items.map((item) => {
            const swatch = withRatings
              ? swatchOf({ colours, category, level: levelOf(item) })
              : colours.quiet;

            return (
              <li key={item.item}>
                <Chip
                  label={
                    withRatings
                      ? t("assessment.summary.footer.withRating", {
                          title: item.title,
                          rating: wordOf(item),
                        })
                      : item.title
                  }
                  size="small"
                  sx={{
                    fontSize: "0.75rem",
                    bgcolor: swatch.background,
                    color: swatch.color,
                    fontWeight: 500,
                  }}
                />
              </li>
            );
          })}
        </Box>
      )}
    </Paper>
  );
};

/**
 * An assessment's answers laid out by what they call for, one column per
 * kind of domain, or the same beside an earlier assessment, each item showing
 * how it moved.
 *
 * Alone, the items below each column's line that a plan may still use follow
 * in strips beneath the columns; a comparison leaves them out, being about
 * what moved in and out of the columns.
 */
export const AssessmentSummary = ({
  summary,
  grouping,
  onGroup,
}: {
  summary: Summary;
  grouping: SummaryGrouping;
  onGroup: (grouping: SummaryGrouping) => Promisable<void>;
}) => {
  const { t } = useTranslation();
  const shown = summary.columns.reduce(
    (count, { shown: items }) => count + items.length,
    0,
  );
  const has = (category: GroupCategory) =>
    summary.columns.some((column) => column.category === category);
  const notAnswered = summary.notAnsweredByDomain.reduce(
    (count, domain) => count + domain.count,
    0,
  );

  const strips: ReactNode[] = summary.comparing
    ? []
    : [
        ...(has("needs")
          ? [
              <Strip
                key="background"
                id="summary-background"
                title={t("assessment.summary.footer.background")}
                rated={t("assessment.summary.footer.ratedOne")}
                items={summary.background}
                category="needs"
                withRatings={false}
              />,
            ]
          : []),
        ...(has("strengths")
          ? [
              <Strip
                key="assets"
                id="summary-assets"
                title={t("assessment.summary.footer.assets")}
                rated={t("assessment.summary.footer.ratedZeroOrOne")}
                items={summary.assets}
                category="strengths"
                withRatings
              />,
            ]
          : []),
        ...(has("caregiver")
          ? [
              <Strip
                key="caregiver-resources"
                id="summary-caregiver-resources"
                title={t("assessment.summary.footer.caregiverResources")}
                rated={t("assessment.summary.footer.ratedZeroOrOne")}
                items={summary.caregiverResources}
                category="caregiver"
                withRatings
              />,
            ]
          : []),
      ];

  const notAnsweredPanel =
    summary.comparing || notAnswered === 0 ? null : (
      <NotAnsweredPanel
        domains={summary.notAnsweredByDomain}
        count={notAnswered}
      />
    );

  return (
    <Stack spacing={2}>
      <Stack
        direction={{ xs: "column", sm: "row" }}
        justifyContent="space-between"
        alignItems={{ xs: "flex-start", sm: "center" }}
        spacing={2}
      >
        <Stack direction="row" alignItems="center" spacing={1.5}>
          <Typography
            id="summary-group-by"
            variant="body2"
            sx={{ fontWeight: 600 }}
          >
            {t("assessment.summary.groupBy")}
          </Typography>
          <ToggleButtonGroup
            exclusive
            size="small"
            color="primary"
            value={grouping}
            aria-labelledby="summary-group-by"
            onChange={(_event, chosen: SummaryGrouping | null) => {
              // An exclusive group reports null for a click on the chosen
              // button, which would leave nothing chosen.
              if (chosen !== null) {
                void onGroup(chosen);
              }
            }}
          >
            {(["rating", "domain"] as const).map((each) => (
              <ToggleButton
                key={each}
                value={each}
                sx={{
                  px: 1.5,
                  py: 0.25,
                  fontSize: "0.8125rem",
                  textTransform: "none",
                  fontWeight: 600,
                  "&.Mui-selected, &.Mui-selected:hover": {
                    bgcolor: "primary.main",
                    color: "primary.contrastText",
                  },
                }}
              >
                {t(`assessment.summary.grouping.${each}`)}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
        </Stack>
        <Typography variant="caption" color="text.secondary">
          {summary.comparing
            ? t("assessment.summary.ratingsOnly")
            : notAnswered === 0
              ? t("assessment.summary.shown", { rated: summary.rated, shown })
              : t("assessment.summary.shownWithNotAnswered", {
                  rated: summary.rated,
                  notAnswered,
                  shown,
                })}
        </Typography>
      </Stack>

      <Box
        sx={{
          display: "grid",
          gap: 2,
          gridTemplateColumns: {
            xs: "1fr",
            md: "repeat(2, minmax(0, 1fr))",
            lg: "repeat(3, minmax(0, 1fr))",
          },
          alignItems: "start",
        }}
      >
        {summary.columns.map((column) => (
          <ColumnCard
            key={column.category}
            column={column}
            grouping={grouping}
            comparing={summary.comparing}
          />
        ))}
      </Box>

      {strips.length === 0 ? null : (
        <Box
          sx={{
            display: "grid",
            gap: 2,
            gridTemplateColumns: { xs: "1fr", md: "repeat(2, minmax(0, 1fr))" },
          }}
        >
          {strips}
        </Box>
      )}

      {notAnsweredPanel}

      <Typography variant="caption" color="text.secondary">
        {summary.comparing
          ? t("assessment.summary.footnote.comparison")
          : t("assessment.summary.footnote.single")}
      </Typography>
    </Stack>
  );
};
